import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";
import type { GridColumnVisibilityModel, GridSortModel } from "@mui/x-data-grid";
import useAsyncRequest from "hooks/useAsyncRequest";
import useFilters from "hooks/useFilters";
import usePagination from "hooks/usePagination";
import useSorting from "hooks/useSorting";
import { nameof } from "utils/nameof";
import { LoadablePage } from "shared/LoadablePage";
import type { UserDto } from "client/api/UsersApi";
import { getUsers } from "client/api/UsersApi";
import { FilterQueryOperator } from "client/api/common/FilterQuery";
import FilterLayout from "components/FilterLayout";
import type { FilterSectionFieldProps } from "components/FilterLayout/FilterSection";
import { FilterSectionType } from "components/FilterLayout/FilterSection";
import { PageContent } from "components/PageContent/PageContent";
import PageHeading from "components/PageHeading";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { UserListTable } from "components/users/UsersListTable";
import { UserRoutesMap } from "./UserRoutesMap";

const userActions = [
  {
    policy: RbacPolicies.Users.ReadWrite,
    component: (
      <Button component={Link} to={UserRoutesMap.add}>
        Add User
      </Button>
    ),
  },
];

const filterSections: FilterSectionFieldProps[] = [
  {
    type: FilterSectionType.Text,
    filterKey: "id",
    label: "User ID",
  },
  {
    type: FilterSectionType.Text,
    filterKey: "firstName",
    label: "First Name",
  },
  {
    type: FilterSectionType.Text,
    filterKey: "lastName",
    label: "Last Name",
  },
  {
    type: FilterSectionType.Text,
    filterKey: "createdByUser",
    label: "Created By User",
  },
  {
    type: FilterSectionType.Date,
    filterKey: "createdDateAfter",
    label: "After Created Date",
  },
  {
    type: FilterSectionType.Date,
    filterKey: "createdDateBefore",
    label: "Before Created Date",
  },
];

const headerFilterSections: FilterSectionFieldProps[] = [
  {
    type: FilterSectionType.Text,
    filterKey: "email",
    label: "Email",
  },
];

const filterMap = {
  id: { operator: FilterQueryOperator.Equal, targetProperty: nameof<UserDto>("id") },
  firstName: { operator: FilterQueryOperator.Like, targetProperty: nameof<UserDto>("firstName") },
  lastName: { operator: FilterQueryOperator.Like, targetProperty: nameof<UserDto>("lastName") },
  createdByUser: { operator: FilterQueryOperator.Like, targetProperty: nameof<UserDto>("createdByUser") },
  createdDateAfter: { operator: FilterQueryOperator.GreaterThan, targetProperty: nameof<UserDto>("createdDate") },
  createdDateBefore: { operator: FilterQueryOperator.LessThan, targetProperty: nameof<UserDto>("createdDate") },
  email: { operator: FilterQueryOperator.Like, targetProperty: nameof<UserDto>("email") },
};

const defaultFilterValue = {};

export function UsersPage() {
  const { page, setPage, pageSize, setPageSize } = usePagination({ initialPageSize: 50 });
  const { filters, filterQuery, setFilter, resetFilter } = useFilters({ filterMap });
  const { sortModel, setSortModel } = useSorting({
    initialSortModel: [
      {
        field: nameof<UserDto>("createdDate"),
        sort: "asc",
      },
    ],
  });
  const defaultColumnVisibility = {};

  const {
    data = { entities: [], totalCount: 0 },
    loading,
    error,
  } = useAsyncRequest(getUsers, {
    skip: page * pageSize,
    take: pageSize,
    sortFieldName: sortModel[0]?.field,
    sortDirection: sortModel[0]?.sort,
    filter: filterQuery,
  });

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(defaultColumnVisibility);

  // Re-sorting reorders the whole result set, so a page held from the old order shows rows nobody
  // asked for — the same hazard as changing a filter.
  const handleSortModelChange = (model: GridSortModel) => {
    setPage(0);
    setSortModel(model);
  };

  return (
    <>
      <PageHeading headingText="Users" actionItems={userActions} />
      <PageContent>
        <LoadablePage error={error}>
          <FilterLayout
            headerFilters={headerFilterSections}
            filterSections={filterSections}
            filter={filters}
            defaultFilter={defaultFilterValue}
            onFilterChange={setFilter}
            onResetFilter={resetFilter}
          >
            <UserListTable
              users={data?.entities}
              totalCount={data?.totalCount}
              page={page}
              pageSize={pageSize}
              loading={loading}
              initialSortModel={sortModel}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onSortModelChange={handleSortModelChange}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
            />
          </FilterLayout>
        </LoadablePage>
      </PageContent>
    </>
  );
}
